/**
 * Health reminders (patient): water / medicine / meal reminders in one list, the medicines on the
 * patient's current prescriptions, and the daily water-intake log. The backend's Arq worker sends
 * the reminders as push notifications; this module only reads and changes them.
 *
 * Times of day are "HH:MM" (24-hour, the patient's local time).
 */

import { apiFetch } from "./client";

export type ReminderKind = "water" | "medicine" | "meal";
export type MealType = "breakfast" | "lunch" | "snacks" | "dinner";
export type AlertStyle = "sound" | "vibrate" | "silent";
export type FoodRelation = "before_food" | "after_food";

export interface HealthReminder {
  id: string;
  kind: ReminderKind;
  title: string;
  note: string | null;
  dosage: string | null;
  food_relation: FoodRelation | null;
  for_family_member_id: string | null;
  for_name: string | null;
  prescription_line_id: string | null;
  meal_type: MealType | null;
  times: string[];
  interval_minutes: 30 | 60 | 120 | null;
  window_start: string | null;
  window_end: string | null;
  start_date: string | null;
  end_date: string | null;
  daily_goal_ml: number | null;
  enabled: boolean;
  alert_style: AlertStyle;
  /** Null while switched off or once the course has ended. */
  next_fire_at: string | null;
  last_fired_at: string | null;
  created_at: string;
  updated_at: string;
}

/** A new reminder. Only its kind's fields count; with `prescription_line_id`, a medicine takes
    whatever is left out from that prescription line. */
export interface HealthReminderInput {
  kind: ReminderKind;
  title?: string | null;
  note?: string | null;
  dosage?: string | null;
  food_relation?: FoodRelation | null;
  for_family_member_id?: string | null;
  prescription_line_id?: string | null;
  meal_type?: MealType | null;
  times?: string[] | null;
  interval_minutes?: 30 | 60 | 120 | null;
  window_start?: string | null;
  window_end?: string | null;
  start_date?: string | null;
  end_date?: string | null;
  daily_goal_ml?: number | null;
  enabled?: boolean;
  alert_style?: AlertStyle;
}

export type HealthReminderPatch = Partial<Omit<HealthReminderInput, "kind" | "prescription_line_id" | "meal_type">>;

export interface MedicineSuggestion {
  prescription_line_id: string;
  prescription_id: string;
  prescription_code: string;
  medicine_name: string;
  dosage: string;
  food_relation: FoodRelation;
  time_of_day: string[];
  times: string[];
  duration_days: number | null;
  for_family_member_id: string | null;
  for_name: string | null;
  issued_at: string;
  already_added: boolean;
}

export interface ReminderTestResult {
  devices: number;
  sent: number;
  push_disabled: boolean;
  /** Push is off for Reminders in Settings → Notification preferences. */
  reminders_off: boolean;
}

export interface WaterIntakeDay {
  day: string;
  amount_ml: number;
}

export interface WaterIntake {
  goal_ml: number;
  glass_ml: number;
  days: WaterIntakeDay[];
}

export const listReminders = (): Promise<HealthReminder[]> => apiFetch("/api/v1/me/health-reminders");

export const createReminder = (body: HealthReminderInput): Promise<HealthReminder> =>
  apiFetch("/api/v1/me/health-reminders", { method: "POST", body });

export const updateReminder = (id: string, body: HealthReminderPatch): Promise<HealthReminder> =>
  apiFetch(`/api/v1/me/health-reminders/${id}`, { method: "PATCH", body });

export const deleteReminder = (id: string): Promise<void> =>
  apiFetch(`/api/v1/me/health-reminders/${id}`, { method: "DELETE" });

/** Push the reminder to every device now, to check it arrives and sounds as chosen. */
export const testReminder = (id: string): Promise<ReminderTestResult> =>
  apiFetch(`/api/v1/me/health-reminders/${id}/test`, { method: "POST" });

export const listMedicineSuggestions = (): Promise<MedicineSuggestion[]> =>
  apiFetch("/api/v1/me/health-reminders/medicine-suggestions");

export const getWaterIntake = (range: { from?: string; to?: string } = {}): Promise<WaterIntake> => {
  const p = new URLSearchParams();
  if (range.from) p.set("from", range.from);
  if (range.to) p.set("to", range.to);
  const qs = p.toString();
  return apiFetch(`/api/v1/me/health-reminders/water-intake${qs ? `?${qs}` : ""}`);
};

/** The day's total (not a delta), so a retried request can't count a glass twice. */
export const setWaterIntake = (day: string, amount_ml: number): Promise<WaterIntakeDay> =>
  apiFetch(`/api/v1/me/health-reminders/water-intake/${day}`, { method: "PUT", body: { amount_ml } });
